import React, { useState, MouseEvent, useId, useEffect, useRef } from 'react';
import {
  PopoverOrigin,
  Menu,
  MenuItem,
  Box,
  Theme,
  SxProps,
  Typography,
  Tooltip,
} from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import Button, { ButtonProps } from './Button';
import MoreIcon from '@watershed/icons/components/More';
import type { IconProps } from '@watershed/icons/Icon';
import ChevronDownIcon from '@watershed/icons/components/ChevronDown';
import ChevronUpIcon from '@watershed/icons/components/ChevronUp';
import must from '@watershed/shared-universal/utils/must';
import { mixinSx } from '@watershed/style/styleUtils';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import IconButton from './IconButton';

export const colorDangerousSx: SystemStyleObject<Theme> = {
  // Extra CSS specificity
  '&&': {
    color: (theme) => theme.palette.error.main,
  },
};

export type DropdownMenuAnalyticsEvent = 'open' | 'close';

export type DropdownItem = {
  id: string;
  testId?: string;
  Icon?: React.ComponentType<React.PropsWithChildren<IconProps>>;
  label: React.ReactNode;
  sublabel?: React.ReactNode;
  onSelect?: (id: string) => void;
  disabled?: boolean;
  variant?: 'primary' | 'dangerous';
  menuItemSx?: SxProps<Theme>;
  innardSx?: SxProps<Theme>;
  labelSx?: SxProps<Theme>;
  tooltip?: string;
  scrollIntoView?: boolean;
};

export function DropdownItemRendered({
  item,
  setAnchorEl,
  minWidth,
  maxWidth = '100%',
}: {
  item: DropdownItem;
  setAnchorEl: (anchor: HTMLButtonElement | null) => void;
  minWidth: number | string;
  maxWidth?: number | string;
}) {
  const ref = useRef<HTMLLIElement | null>(null);
  const handleClick = (event: MouseEvent) => {
    event.preventDefault();
    must(item.onSelect)(item.id);
    setAnchorEl(null);
    event.stopPropagation();
  };

  useEffect(() => {
    if (item.scrollIntoView) {
      ref.current?.scrollIntoView();
    }
  }, [item.scrollIntoView]);

  const innards = item.Icon ? (
    <Box
      sx={mixinSx(
        {
          display: 'flex',
          alignItems: 'center',
        },
        item.innardSx
      )}
    >
      <Box flexShrink={0} display="flex">
        <item.Icon
          size={20}
          sx={mixinSx(
            {
              color: (theme) => theme.palette.text.secondary,
              display: 'block',
            },
            item.variant === 'dangerous' ? colorDangerousSx : undefined
          )}
        />
      </Box>
      <Box flexGrow={1} marginLeft={0.5} sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" sx={item.labelSx}>
          {item.label}
        </Typography>
        <Typography variant="caption">{item.sublabel}</Typography>
      </Box>
    </Box>
  ) : (
    <Box sx={item.innardSx}>
      <Typography variant="subtitle1" sx={item.labelSx}>
        {item.label}
      </Typography>
      <Typography variant="caption">{item.sublabel}</Typography>
    </Box>
  );

  const menuItem = (
    <MenuItem
      data-test={item.testId}
      key={item.id}
      onClick={item.onSelect ? handleClick : undefined}
      style={{ minWidth, maxWidth }}
      disabled={item.disabled}
      sx={mixinSx(
        { minHeight: 0 },
        item.menuItemSx,
        item.variant === 'dangerous' ? colorDangerousSx : undefined
      )}
      ref={ref}
    >
      {innards}
    </MenuItem>
  );

  // For tooltips to work on MenuItems that are disabled, we have to wrap the MenuItem in a div.
  // See https://stackoverflow.com/questions/63691972/react-material-ui-tooltip-hover-overfire-an-event-trigger-when-the-item-is-disa
  return item.tooltip ? (
    <Tooltip title={item.tooltip}>
      <Box
        sx={{
          // We apply `borderTop: none` to all first-of-type MenuItems, so we need
          // to re-apply this border here. See
          // https://github.com/watershed-climate/ghg/blob/2ebc6b5dd06c54e3bc50ddc1f9584e1abb1a4391/workspaces/shared-frontend/styles/themes/default.tsx#L831
          borderTop: (theme) => `1px solid ${theme.palette.border}`,
        }}
      >
        {menuItem}
      </Box>
    </Tooltip>
  ) : (
    menuItem
  );
}

export function DropdownTrigger({
  trigger,
  triggerStartIcon,
  triggerEndIcon,
  menuElementId,
  handleClick,
  buttonProps,
  open,
  testId,
}: {
  trigger?: React.ReactNode;
  triggerStartIcon?: React.ReactNode;
  triggerEndIcon?: React.ReactNode;
  menuElementId: string;
  handleClick: (event: MouseEvent<HTMLButtonElement>) => void;
  buttonProps?: Pick<
    ButtonProps,
    'size' | 'color' | 'isIcon' | 'variant' | 'tooltip' | 'disabled'
  >;
  open: Boolean;
  testId?: string;
}) {
  return trigger ? (
    <Button
      data-test={testId ?? TestIds.DropdownMenuButton}
      aria-label="Actions"
      aria-controls={menuElementId}
      aria-haspopup="true"
      onClick={handleClick}
      startIcon={triggerStartIcon}
      endIcon={
        triggerEndIcon !== undefined ? (
          triggerEndIcon
        ) : open ? (
          <ChevronUpIcon />
        ) : (
          <ChevronDownIcon />
        )
      }
      {...buttonProps}
      size={buttonProps?.size ?? 'medium'}
    >
      {trigger}
    </Button>
  ) : (
    <IconButton
      data-test={testId ?? TestIds.DropdownMenuButton}
      aria-label="Actions"
      aria-controls={menuElementId}
      aria-haspopup="true"
      onClick={handleClick}
      {...buttonProps}
      size={buttonProps?.size ?? 'large'}
    >
      {triggerEndIcon ?? <MoreIcon />}
    </IconButton>
  );
}

/**
 * A dropdown menu with some of the standard functionality that you want pretty
 * much every time you use a dropdown menu.
 *
 * - Opens and closes without you having to manage that state.
 * - Closes after you make a selection.
 * - Renders a text button trigger or a three-dot-icon trigger.
 */
export default function DropdownMenu({
  testId,
  trigger,
  triggerStartIcon,
  triggerEndIcon,
  items,
  minWidth = 150,
  paperMaxHeight,
  isDivided = false,
  anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin = {
    vertical: 'top',
    horizontal: 'left',
  },
  onAnalyticsEvent = () => {},
  keepMounted = true,
  buttonProps,
  tooltip,
  menuSx,
}: {
  testId?: string;
  /**
   * If trigger is not provided, a three-dot-icon button is used.
   */
  trigger?: React.ReactNode;
  triggerStartIcon?: React.ReactNode;
  triggerEndIcon?: React.ReactNode;
  minWidth?: number | string;
  paperMaxHeight?: number;
  /**
   * By default the menu opens bottom and left-aligned the anchor element, but it
   * can it be modified with anchor/transformOrigin. This playground is useful:
   * https://mui.com/components/popover/#anchor-playground
   */
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  /**
   * If true, adds dividers between menu items.
   */
  isDivided?: boolean;
  /**
   * The component passed as `Icon` is rendered in front of text with some
   * standard styling: if you need more customization, do it with `label`.
   */
  items: Array<DropdownItem>;
  /**
   * Passes through to MUI Menu's keepMounted property
   */
  keepMounted?: boolean;
  onAnalyticsEvent?: (event: DropdownMenuAnalyticsEvent) => void;
  buttonProps?: Pick<
    ButtonProps,
    | 'size'
    | 'color'
    | 'isIcon'
    | 'variant'
    | 'tooltip'
    | 'disabled'
    | 'startIcon'
    | 'endIcon'
    | 'sx'
  >;
  tooltip?: string;
  menuSx?: SxProps<Theme>;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const open = Boolean(anchorEl);
  // Create a unique ID for this instance that can be used for the accessibility
  // props.
  const menuElementId = useId();

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    onAnalyticsEvent('open');
    event.stopPropagation();
  };

  const handleClose = (event: MouseEvent) => {
    event.preventDefault();
    setAnchorEl(null);
    onAnalyticsEvent('close');
    event.stopPropagation();
  };

  const renderedItems = items.map((item) => {
    return (
      <DropdownItemRendered
        key={`dropdownItemRendered-${item.id}`}
        item={item}
        setAnchorEl={setAnchorEl}
        minWidth={minWidth}
      />
    );
  });

  const renderedTrigger = (
    <DropdownTrigger
      testId={testId}
      trigger={trigger}
      triggerStartIcon={triggerStartIcon}
      triggerEndIcon={triggerEndIcon}
      menuElementId={menuElementId}
      handleClick={handleClick}
      buttonProps={buttonProps}
      open={open}
    />
  );

  return (
    <>
      {tooltip ? (
        <Tooltip title={tooltip}>
          <span>{renderedTrigger}</span>
        </Tooltip>
      ) : (
        renderedTrigger
      )}
      <Menu
        id={menuElementId}
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        keepMounted={keepMounted}
        open={open}
        onClose={handleClose}
        transitionDuration={{ enter: 0, exit: 300 }}
        sx={mixinSx(
          {
            '& .MuiMenu-paper': {
              maxHeight: paperMaxHeight
                ? `${paperMaxHeight}px !important`
                : undefined,
            },
            '& .MuiMenu-list': isDivided
              ? {
                  '&>:not(:last-child)': {
                    borderBottom: (theme) =>
                      '1px solid ' + theme.palette.divider,
                  },
                }
              : null,
          },
          menuSx
        )}
      >
        {renderedItems}
      </Menu>
    </>
  );
}
